import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, X, Stack } from "@phosphor-icons/react";

export default function Categories() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({ name: "", description: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/categories");
    setItems(data);
  };

  useEffect(() => { load(); }, []);

  const onOpen = (c) => {
    setEdit(c || null);
    setForm(c ? { name: c.name, description: c.description || "" } : { name: "", description: "" });
    setOpen(true);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (edit) await api.patch(`/categories/${edit.id}`, form);
      else await api.post("/categories", form);
      toast.success(edit ? "Category updated" : "Category created");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (c) => {
    if (!window.confirm(`Delete category "${c.name}"?`)) return;
    try {
      await api.delete(`/categories/${c.id}`);
      toast.success("Category deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#FF9800]">Taxonomy</div>
          <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-zinc-900 mt-1">
            Categories
          </h1>
        </div>
        <button
          onClick={() => onOpen(null)}
          data-testid="add-category-button"
          className="inline-flex items-center gap-2 bg-[#FF9800] hover:bg-[#E68900] text-white px-4 sm:px-5 py-2 sm:py-2.5 text-xs sm:text-sm font-semibold w-fit"
        >
          <Plus size={16} weight="bold" /> Add category
        </button>
      </div>

      <div className="bg-white border border-zinc-300 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
              <th className="text-left px-3 sm:px-4 py-3 border-b-2 border-zinc-900">Name</th>
              <th className="text-left px-3 sm:px-4 py-3 border-b-2 border-zinc-900 hidden sm:table-cell">Description</th>
              <th className="text-right px-3 sm:px-4 py-3 border-b-2 border-zinc-900 w-24 sm:w-32">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr>
                <td colSpan={3} className="px-4 py-12 text-center">
                  <Stack size={28} weight="duotone" className="mx-auto text-zinc-300" />
                  <div className="text-sm text-zinc-500 mt-2">No categories.</div>
                </td>
              </tr>
            )}
            {items.map((c) => (
              <tr key={c.id} className="row-hover" data-testid={`category-row-${c.name.toLowerCase().replace(/\s+/g, "-")}`}>
                <td className="px-3 sm:px-4 py-3 border-b border-zinc-200 font-medium">
                  <div>{c.name}</div>
                  <div className="text-[11px] text-zinc-500 sm:hidden mt-0.5">{c.description || "—"}</div>
                </td>
                <td className="px-3 sm:px-4 py-3 border-b border-zinc-200 text-zinc-600 hidden sm:table-cell">{c.description || "—"}</td>
                <td className="px-3 sm:px-4 py-3 border-b border-zinc-200 text-right">
                  <div className="inline-flex items-center divide-x divide-zinc-300 border border-zinc-300">
                    <button onClick={() => onOpen(c)} data-testid={`edit-category-${c.id}`} className="px-2.5 py-1.5 hover:bg-[#1A2332] hover:text-white">
                      <PencilSimple size={14} weight="bold" />
                    </button>
                    <button onClick={() => onDelete(c)} data-testid={`delete-category-${c.id}`} className="px-2.5 py-1.5 hover:bg-red-600 hover:text-white">
                      <Trash size={14} weight="bold" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-[#1A2332]/60 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white border border-zinc-300 w-full max-w-md">
            <div className="flex items-center justify-between border-b border-zinc-300 px-6 py-4">
              <h2 className="font-display text-xl font-semibold">{edit ? "Edit category" : "New category"}</h2>
              <button onClick={() => setOpen(false)} className="p-1 hover:bg-zinc-100"><X size={18} weight="bold" /></button>
            </div>
            <form onSubmit={onSubmit} className="px-6 py-5 space-y-4">
              <label className="block">
                <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-700">Name *</span>
                <input
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="category-name-input"
                  className="mt-1.5 w-full px-4 py-2.5 border border-zinc-300 focus:border-[#FF9800] focus:outline-none focus:ring-1 focus:ring-[#FF9800]"
                />
              </label>
              <label className="block">
                <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-700">Description</span>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  data-testid="category-description-input"
                  className="mt-1.5 w-full px-4 py-2.5 border border-zinc-300 focus:border-[#FF9800] focus:outline-none focus:ring-1 focus:ring-[#FF9800] min-h-[80px]"
                />
              </label>
              <div className="flex items-center justify-end gap-2 pt-2 border-t border-zinc-200">
                <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 text-sm font-semibold border border-zinc-300 hover:bg-zinc-100">Cancel</button>
                <button type="submit" disabled={saving} data-testid="category-save-button" className="px-5 py-2 text-sm font-semibold bg-[#FF9800] hover:bg-[#E68900] text-white">
                  {saving ? "Saving…" : edit ? "Save" : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
